import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { 
  SpotifyPlaylist, 
  PlaylistsResponse, 
  CreatePlaylistRequest,
  PlaylistTrack,
  SpotifyTrack,
  CurrentlyPlayingResponse,
  RecentlyPlayedResponse,
  RecentlyPlayedTrack,
  PlaylistComparisonResult
} from '../../shared/models/spotify.models';

@Injectable({
  providedIn: 'root'
})
export class SpotifyService {
  private apiBaseUrl = environment.apiBaseUrl;
  private playlistsSubject = new BehaviorSubject<SpotifyPlaylist[]>([]);
  public playlists$ = this.playlistsSubject.asObservable();

  constructor(private http: HttpClient) {}

  // Playlist Management
  getUserPlaylists(limit: number = 50, offset: number = 0): Observable<PlaylistsResponse> {
    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    return this.http.get<PlaylistsResponse>(`${this.apiBaseUrl}/spotify/playlists`, { params });
  }

  getPlaylist(playlistId: string): Observable<SpotifyPlaylist> {
    return this.http.get<SpotifyPlaylist>(`${this.apiBaseUrl}/spotify/playlists/${playlistId}`);
  }

  getPlaylistTracks(playlistId: string, limit: number = 50, offset: number = 0): Observable<PlaylistTrack[]> {
    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    return this.http.get<{items: PlaylistTrack[]}>(`${this.apiBaseUrl}/spotify/playlists/${playlistId}/tracks`, { params })
      .pipe(map(response => response.items));
  }

  createPlaylist(playlist: CreatePlaylistRequest): Observable<SpotifyPlaylist> {
    return this.http.post<SpotifyPlaylist>(`${this.apiBaseUrl}/spotify/playlists`, playlist);
  }

  updatePlaylist(playlistId: string, updates: Partial<CreatePlaylistRequest>): Observable<void> {
    return this.http.put<void>(`${this.apiBaseUrl}/spotify/playlists/${playlistId}`, updates);
  }

  deletePlaylist(playlistId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/spotify/playlists/${playlistId}/followers`);
  }

  // Track Management
  addTracksToPlaylist(playlistId: string, trackUris: string[]): Observable<{snapshot_id: string}> {
    return this.http.post<{snapshot_id: string}>(
      `${this.apiBaseUrl}/spotify/playlists/${playlistId}/tracks`,
      { uris: trackUris }
    );
  }

  removeTracksFromPlaylist(playlistId: string, trackUris: string[]): Observable<{snapshot_id: string}> {
    const tracks = trackUris.map(uri => ({ uri }));
    return this.http.delete<{snapshot_id: string}>(
      `${this.apiBaseUrl}/spotify/playlists/${playlistId}/tracks`,
      { body: { tracks } }
    );
  }

  reorderPlaylistTracks(
    playlistId: string, 
    rangeStart: number, 
    insertBefore: number, 
    rangeLength: number = 1
  ): Observable<{snapshot_id: string}> {
    return this.http.put<{snapshot_id: string}>(
      `${this.apiBaseUrl}/spotify/playlists/${playlistId}/tracks`,
      {
        range_start: rangeStart,
        insert_before: insertBefore,
        range_length: rangeLength
      }
    );
  }

  // Search
  searchTracks(query: string, limit: number = 20): Observable<SpotifyTrack[]> {
    const params = new HttpParams()
      .set('q', query)
      .set('type', 'track')
      .set('limit', limit.toString());

    return this.http.get<{tracks: {items: SpotifyTrack[]}}>(`${this.apiBaseUrl}/spotify/search`, { params })
      .pipe(map(response => response.tracks.items));
  }

  // Listening History
  getCurrentlyPlaying(): Observable<CurrentlyPlayingResponse> {
    return this.http.get<CurrentlyPlayingResponse>(`${this.apiBaseUrl}/spotify/currently-playing`);
  }

  getRecentlyPlayed(limit: number = 50): Observable<RecentlyPlayedResponse> {
    const params = new HttpParams().set('limit', limit.toString());
    return this.http.get<RecentlyPlayedResponse>(`${this.apiBaseUrl}/spotify/recently-played`, { params });
  }

  getListeningHistory(limit: number = 50, filterArtist?: string, filterTrack?: string): Observable<any> {
    let params = new HttpParams().set('limit', limit.toString());

    if (filterArtist) {
      params = params.set('filterArtist', filterArtist);
    }

    if (filterTrack) {
      params = params.set('filterTrack', filterTrack);
    }

    return this.http.get<any>(`${this.apiBaseUrl}/listening-history/history`, { params });
  }

  // Playlist Comparison and Management
  comparePlaylistWithRecentTracks(playlistId: string, timeWindowDays: number = 7, maximumPlayCount: number = 1): Observable<PlaylistComparisonResult> {
    // Get playlist tracks and recent tracks in parallel
    const playlistTracks$ = this.getPlaylistTracks(playlistId, 50, 0);
    const recentTracks$ = this.getRecentlyPlayed(50);

    return combineLatest([playlistTracks$, recentTracks$]).pipe(
      map(([playlistTracks, recentResponse]) => {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - timeWindowDays);

        // Filter recent tracks by time window
        const filteredRecentTracks = recentResponse.items.filter(item => 
          new Date(item.played_at) > cutoffDate
        );

        // Count how many times each track was played
        const playCountMap = new Map<string, number>();
        filteredRecentTracks.forEach(item => {
          const trackId = item.track.id;
          playCountMap.set(trackId, (playCountMap.get(trackId) || 0) + 1);
        });

        // Find tracks that meet the maximum play count requirement
        const qualifyingTrackIds = Array.from(playCountMap.entries())
          .filter(([trackId, count]) => count <= maximumPlayCount)
          .map(([trackId, count]) => trackId);

        const recentTrackIds = new Set(qualifyingTrackIds);
        const matchedTrackIds = playlistTracks
          .filter(item => recentTrackIds.has(item.track.id))
          .map(item => item.track.id);

        return {
          playlistTracks,
          recentTracks: filteredRecentTracks,
          matchedTrackIds,
          timeWindow: timeWindowDays
        };
      })
    );
  }

  removeRecentTracksFromPlaylist(playlistId: string, timeWindowDays: number = 7, maximumPlayCount: number = 1): Observable<{snapshot_id: string}> {
    return this.comparePlaylistWithRecentTracks(playlistId, timeWindowDays, maximumPlayCount).pipe(
      map((comparison: PlaylistComparisonResult) => {
        const tracksToRemove = comparison.playlistTracks
          .filter(item => comparison.matchedTrackIds.includes(item.track.id))
          .map(item => item.track.uri);
        
        return tracksToRemove;
      }),
      // Switch to removal operation if there are tracks to remove
      switchMap((trackUris: string[]) => {
        if (trackUris.length === 0) {
          return of({snapshot_id: ''});
        }
        return this.removeTracksFromPlaylist(playlistId, trackUris);
      })
    );
  }

  // State Management
  refreshPlaylists(): void {
    this.getUserPlaylists().subscribe(
      response => this.playlistsSubject.next(response.items),
      error => console.error('Error fetching playlists:', error)
    );
  }

  // Utility Methods
  getPlaylistImageUrl(playlist: SpotifyPlaylist, size: 'small' | 'medium' | 'large' = 'medium'): string | null {
    if (!playlist.images || playlist.images.length === 0) {
      return null; // Return null when no image is available
    }

    // Sort images by size and pick appropriate one
    const sortedImages = playlist.images.sort((a, b) => (b.width || 0) - (a.width || 0));
    
    switch (size) {
      case 'small':
        return sortedImages[sortedImages.length - 1]?.url || sortedImages[0].url;
      case 'large':
        return sortedImages[0].url;
      case 'medium':
      default:
        const midIndex = Math.floor(sortedImages.length / 2);
        return sortedImages[midIndex]?.url || sortedImages[0].url;
    }
  }

  hasPlaylistImage(playlist: SpotifyPlaylist): boolean {
    return !!(playlist.images && playlist.images.length > 0);
  }

  formatDuration(durationMs: number): string {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  formatPlaylistDuration(tracks: PlaylistTrack[]): string {
    const totalMs = tracks.reduce((sum, item) => sum + item.track.duration_ms, 0);
    const totalMinutes = Math.floor(totalMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  // Playback API endpoints (these communicate with backend which proxies to Spotify)
  getAvailableDevices(): Observable<{devices: any[]}> {
    return this.http.get<{devices: any[]}>(`${this.apiBaseUrl}/spotify/devices`);
  }

  getCurrentPlaybackState(): Observable<any> {
    return this.http.get<any>(`${this.apiBaseUrl}/spotify/player`);
  }

  startPlayback(deviceId?: string, contextUri?: string, uris?: string[], offset?: number | { uri: string }, positionMs?: number): Observable<void> {
    const body: any = {};
    if (deviceId) body.device_id = deviceId;
    if (contextUri) body.context_uri = contextUri;
    if (uris && uris.length > 0) body.uris = uris;
    if (offset !== undefined) { body.offset = typeof offset === 'number' ? { position: offset } : offset; }
    if (positionMs !== undefined) body.position_ms = positionMs;

    return this.http.put<void>(`${this.apiBaseUrl}/spotify/player/play`, body);
  }

  pausePlayback(deviceId?: string): Observable<void> {
    const body: any = {};
    if (deviceId) body.device_id = deviceId;
    return this.http.put<void>(`${this.apiBaseUrl}/spotify/player/pause`, body);
  }

  nextTrack(deviceId?: string): Observable<void> {
    const body: any = {};
    if (deviceId) body.device_id = deviceId;
    return this.http.post<void>(`${this.apiBaseUrl}/spotify/player/next`, body);
  }

  previousTrack(deviceId?: string): Observable<void> {
    const body: any = {};
    if (deviceId) body.device_id = deviceId;
    return this.http.post<void>(`${this.apiBaseUrl}/spotify/player/previous`, body);
  }

  seekToPosition(positionMs: number, deviceId?: string): Observable<void> {
    const body: any = { position_ms: positionMs };
    if (deviceId) body.device_id = deviceId;
    return this.http.put<void>(`${this.apiBaseUrl}/spotify/player/seek`, body);
  }

  setVolume(volumePercent: number, deviceId?: string): Observable<void> {
    const body: any = { volume_percent: volumePercent };
    if (deviceId) body.device_id = deviceId;
    return this.http.put<void>(`${this.apiBaseUrl}/spotify/player/volume`, body);
  }

  setShuffle(state: boolean, deviceId?: string): Observable<void> {
    const body: any = { state };
    if (deviceId) body.device_id = deviceId;
    return this.http.put<void>(`${this.apiBaseUrl}/spotify/player/shuffle`, body);
  }

  setRepeatMode(state: 'track' | 'context' | 'off', deviceId?: string): Observable<void> {
    const body: any = { state };
    if (deviceId) body.device_id = deviceId;
    return this.http.put<void>(`${this.apiBaseUrl}/spotify/player/repeat`, body);
  }

  transferPlayback(deviceIds: string[], play: boolean = false): Observable<void> {
    return this.http.put<void>(`${this.apiBaseUrl}/spotify/player`, {
      device_ids: deviceIds,
      play
    });
  }

  // Check premium status via dedicated endpoint
  checkPremiumStatus(): Observable<{isPremium: boolean}> {
    return this.http.get<{isPremium: boolean}>(`${this.apiBaseUrl}/spotify/premium-status`);
  }
}